import React, { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InputOTP, InputOTPGroup, InputOTPSlot, Button } from "@envirohub/ui";
import { ArrowLeft } from "lucide-react";
import { auth } from "@envirohub/utils";
import { useVerifyOtp, useResendOtp } from "@/api";
import { useAuthError } from "@/hooks/useAuthError";

export default function OtpPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const contact = location.state?.contact || "your device";
  const redirectTo = location.state?.redirectTo ?? "/";

  const resolveAuthError = useAuthError();

  const [value, setValue] = React.useState("");
  const [countdown, setCountdown] = React.useState(60);
  const [message, setMessage] = React.useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  // OTP_MAX_ATTEMPTS blocks further verification until a new code is requested.
  const [maxAttemptsReached, setMaxAttemptsReached] = React.useState(false);

  const verifyOtp = useVerifyOtp();
  const resendOtp = useResendOtp();

  // Handle resend countdown timer
  React.useEffect(() => {
    if (countdown === 0) return undefined;
    const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleVerify = useCallback(() => {
    if (value.length < 4 || verifyOtp.isPending || maxAttemptsReached) return;
    setMessage(null);
    verifyOtp.mutate(
      { email: contact, code: value },
      {
        onSuccess: () => {
          if (redirectTo === "/login") auth.clearAuth();
          navigate(redirectTo);
        },
        onError: (error) => {
          const action = resolveAuthError(error);
          // OTP_MAX_ATTEMPTS -> block verification, prompt a new code.
          if (action.code === "OTP_MAX_ATTEMPTS") {
            setMaxAttemptsReached(true);
          }
          // OTP_INVALID / generic -> inline error, clear the input.
          setMessage({ type: "error", text: action.message });
          setValue("");
        },
      },
    );
  }, [value, verifyOtp, maxAttemptsReached, contact, redirectTo, navigate, resolveAuthError]);

  // Automatically submit when OTP is fully entered
  React.useEffect(() => {
    if (value.length === 4) {
      handleVerify();
    }
  }, [value, handleVerify]);

  const handleResend = () => {
    if (countdown > 0 || resendOtp.isPending) return;
    resendOtp.mutate(contact, {
      onSuccess: () => {
        setMessage({
          type: "success",
          text: t("auth.resendOtpSuccess", {
            defaultValue: "Verification code resent!",
          }),
        });
        setCountdown(60);
        // A fresh code resets the attempt limit.
        setMaxAttemptsReached(false);
        setValue("");
      },
      onError: (error) => {
        const action = resolveAuthError(error);
        setMessage({ type: "error", text: action.message });
      },
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <button
        type="button"
        onClick={() => navigate("/login")}
        className="self-start flex items-center gap-1.5 text-body-sm font-medium text-surface-on-variant dark:text-[#C3C9B4] hover:text-surface-on dark:hover:text-white hover:underline transition-all"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("common.back")}
      </button>

      <div>
        <h1 className="text-h3 font-bold text-surface-on dark:text-white">{t("auth.otp")}</h1>
        <p className="text-body text-surface-on-variant dark:text-[#C3C9B4] mt-1.5">
          {t("auth.otpDescription", {
            defaultValue: "Enter the 4-digit verification code sent to your contact device.",
          })}
        </p>
        <p className="text-body-sm font-semibold text-primary mt-1">{contact}</p>
      </div>

      <div className="flex flex-col items-center gap-4" style={{ direction: "ltr" }}>
        <InputOTP
          maxLength={4}
          value={value}
          onChange={setValue}
          disabled={verifyOtp.isPending || maxAttemptsReached}
        >
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
          </InputOTPGroup>
        </InputOTP>

        {message && (
          <p
            className={`text-xs font-semibold ${message.type === "success" ? "text-success" : "text-error"}`}
          >
            {message.text}
          </p>
        )}
      </div>

      <Button
        variant="default"
        size="lg"
        className="w-full shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-shadow"
        disabled={value.length < 4 || maxAttemptsReached}
        loading={verifyOtp.isPending}
        onClick={handleVerify}
      >
        {t("common.confirm")}
      </Button>

      <div className="text-center mt-2">
        <button
          type="button"
          onClick={handleResend}
          disabled={countdown > 0}
          className={`text-body-sm font-bold no-underline hover:underline transition-all ${
            countdown > 0
              ? "text-surface-on-variant dark:text-[#C3C9B4] opacity-60 cursor-not-allowed"
              : "text-primary"
          }`}
        >
          {t("auth.resendOtp")} {countdown > 0 && `(${countdown}s)`}
        </button>
      </div>
    </div>
  );
}
